import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback, useMemo } from 'react';

import useMobileScreen from '../../lib/hooks/useMobileScreen';
import BackActionButton from '../BackActionButton';
import IconChevronDown from '../Icons/IconChevronDown';
import IconChevronUp from '../Icons/IconChevronUp';

import buildNavGroup from './buildNavGroup';
import ProgressStep from './ProgressStep';

import './ProgressBar.scss';

const calculateStepProgression = (group, lastPath, wizardLastPath) => {
  let progress = 0;
  let active = false;
  const wizardSec = parseInt(wizardLastPath.sec, 10);
  const { paths, order } = group;
  const groupLength = paths.length;

  if (order < wizardSec) {
    active = true;
    progress = groupLength;
  }
  if (order === wizardSec) {
    active = true;
    progress = paths.indexOf(lastPath);
  }

  return { active, progress };
};

export const ProgressBar = ({ wizardPaths, lastPath, currentPathIndex, backAction }) => {
  const isMobile = useMobileScreen();
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [activeGroupIndex, setActiveGroupIndex] = useState(null);

  const buildNav = useCallback(() => {
    const builtSteps = wizardPaths.reduce(buildNavGroup, []);
    return builtSteps.map((stepObj, index) => {
      const step = stepObj;
      step.order = index + 1;
      step.final = index === builtSteps.length - 1;
      return step;
    });
  }, [wizardPaths]);

  const [groupedNavigationSteps, setGroupedNavigationSteps] = useState([]);

  useEffect(() => {
    const builtSteps = buildNav();
    let currentActiveGroupIndex = null;
    if (currentPathIndex < 1) return;
    const stepsWithProgress = builtSteps.map((group, index) => {
      const progression = calculateStepProgression(group, lastPath, wizardPaths[currentPathIndex]);
      if (progression.active) {
        currentActiveGroupIndex = index;
      }
      return { ...group, ...progression };
    });

    setGroupedNavigationSteps(stepsWithProgress);
    setActiveGroupIndex(currentActiveGroupIndex);
  }, [wizardPaths, lastPath, currentPathIndex, buildNav]);

  const activeGroup = useMemo(() => groupedNavigationSteps[activeGroupIndex], [
    groupedNavigationSteps,
    activeGroupIndex,
  ]);
  return (
    <div className="progress-container">
      {currentPathIndex > 1 && wizardPaths[currentPathIndex] && (
        <div className="progress-section flex justify-between gap-4">
          {isMobile && <BackActionButton action={backAction} />}
          {isMobile && !isOpenMenu && activeGroup ? (
            <div className="progress-header flex items-center justify-between flex-1">
              <ProgressStep
                {...activeGroup}
                currentIndex={activeGroupIndex}
                activeGroupIndex={activeGroupIndex}
              />
            </div>
          ) : (
            <div className="progress-items flex flex-col gap-6">
              {groupedNavigationSteps.map((group, index) => (
                <ProgressStep
                  key={group.order}
                  {...group}
                  currentIndex={index}
                  activeGroupIndex={activeGroupIndex}
                />
              ))}
            </div>
          )}
          {isMobile && (
            <button
              onClick={() => setIsOpenMenu(prevState => !prevState)}
              className={`flex nav-button ${isOpenMenu ? 'up' : 'down'}`}
              aria-label="toggle button"
            >
              {isOpenMenu ? <IconChevronUp /> : <IconChevronDown />}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

ProgressBar.propTypes = {
  wizardPaths: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentPathIndex: PropTypes.number,
  lastPath: PropTypes.string,
  backAction: PropTypes.func,
};

export default ProgressBar;
